import dayjs, { isDayjs, type Dayjs } from "dayjs"
import isBetween from "dayjs/plugin/isBetween"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import minMax from "dayjs/plugin/minMax"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"

import { type Appointment } from "@/graphql/types"

import { Weekday } from "./calendar"

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault(dayjs.tz.guess())
dayjs.extend(isBetween)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(minMax)

export const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/
export const LOCALE_DATE_REGEX = /^\d{1,2}\.\d{1,2}\.\d{4}$/
export const TIME_REGEX = /^(?:(?:[01]?\d)|(?:2[0-3])):[0-5]\d$/
export const TIME_REGEX_OPTIONAL = /^(?:(?:(?:[01]?\d)|(?:2[0-3])):[0-5]\d)?$/
export const NAIVE_TIME_REGEX = /^(?:(?:[01]?\d)|(?:2[0-3])):[0-5]\d((?::[0-5]\d)(?:.\d\d\d)?)?$/
export const ISO_DATE_WITHOUT_YEAR_REGEX = /^-\d{2}-\d{2}$/
export const millisecondsPerDay = 1000 * 60 * 60 * 24
export type DateInput = string | Date | Dayjs | null

/**
 * Pads a number or a string with `"0"` until the string reaches the desired length.
 * @param val A number or any other character.
 * @param length The final length of the string. Defaults to `2`.
 *        If the string is longer than this value, then the string will be returned as it is.
 * @returns The padded string.
 */
export function padZero(val: number | string, length = 2): string {
  return String(val).padStart(length, "0")
}

/**
 * Converts a date string in locale format ('DD.MM.YYYY') to 'YYYY-MM-DD' format.
 */
export const ISOToLocaleString = (input: string) => {
  if (!ISO_DATE_REGEX.test(input)) {
    return input
  }

  const [year, month, day] = input.split("-")
  return `${padZero(day)}.${padZero(month)}.${year}`
}

/**
 * Formats a `Dayjs` object in 'YYYY-MM-DD' format.
 */
export const formatISO = (date: Dayjs) => {
  return date.format().split("T")[0]
}

export const localeToISOString = (input: string): string => {
  if (!LOCALE_DATE_REGEX.test(input)) {
    return input
  }

  const [days, month, year] = input.split(".")
  return `${year}-${padZero(month)}-${padZero(days)}`
}
/**
 * Tests if the given string is a valid time representation of the form hh:mm
 */
export const isValidTimeString = (value: string | null | undefined) => {
  return value ? TIME_REGEX.test(value) : false
}

/**
 * Tests if the given string is a valid time representation of the form hh:mm:ss.iii ((milli)seconds optional)
 */
export const isValidNaiveTimeString = (value: string | null | undefined) => {
  return value ? NAIVE_TIME_REGEX.test(value) : false
}

/**
 * Parses a (naive) time string into a `Dayjs` object, if the time does not belong to a concrete date.
 */
export const parseTime = (str: string) => {
  const date = dayjs(0)
  // we ignore seconds and milliseconds here because we don't ever need that precision
  const [hours, minutes, _seconds] = str.split(":").map(Number)
  return date.hour(hours).minute(minutes)
}

export function minutesToHhMm(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${padZero(h)}:${padZero(m)}`
}

/**
 * A helper function to split the time to hour and minute
 */
export const splitTime = (timeStr: string): { hour: number; minute: number } => {
  const [hour, minute] = timeStr.split(":").map(Number)
  return { hour, minute }
}

/**
 *Validator to check if the time is valid
 */
export const isValidTime = (value: string): boolean => {
  const { hour, minute } = splitTime(value)
  return hour >= 0 && hour <= 23 && minute >= 0 && minute < 60
}

/**
 *Validator to check if endTime is after beginTime
 */
export const isTimeAfterTime = (
  beginTime: string | undefined,
  endTime: string | undefined
): boolean => {
  if (!beginTime || !endTime) {
    return true
  }
  const begin = splitTime(beginTime)
  const end = splitTime(endTime)

  const beginInMinutes = begin.hour * 60 + begin.minute
  const endInMinutes = end.hour * 60 + end.minute

  return endInMinutes > beginInMinutes
}

/**
 *Id Generator for mock data
 */
export function generateUniqueId() {
  return `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/** Constructs a Dayjs object, but avoids costly cloning if it already is one. */
export function ensureDayjs(date: string | Date | Dayjs) {
  return isDayjs(date) ? date : dayjs(date)
}

/**
 * Tests if a given value is a valid date object
 * @param date The value to test
 * @returns If the value is a valid date
 */
export function isDate(date: unknown): date is Date {
  return date instanceof Date && !isInvalid(date)
}

/**
 * Tests if a date is invalid.
 * @param date The date to test
 * @returns `true` if the date is invalid, `false` otherwise
 */
export function isInvalid(date: Date): boolean {
  return date.getTime() == null || Number.isNaN(date.getTime())
}

/**
 * Tests if a native date is valid.
 * @param dateString The date to test (e.g `1994-11-13` | `2000-02-24`)
 * @returns true if the date is valid
 */
export function isValidNativeDate(dateString: unknown): boolean {
  if (dateString === null || dateString === undefined) return false
  if (typeof dateString !== "string") return false
  if (dateString.trim() === "") return false
  const regex = /^\d{4}-\d{2}-\d{2}$/
  return regex.test(dateString) && isDate(new Date(dateString))
}

/**
 * Tries to convert a string to a date object.
 * If the input is not in yyyy-MM-dd format, the input is returned as it is.
 * If the input is an invalid date, an empty string will be returned.
 * @param input The date string
 * @returns The string or date object
 */
export function stringToDate(input: string): Date | string {
  if (input === "0000-00-00") {
    return ""
  }

  if (ISO_DATE_WITHOUT_YEAR_REGEX.test(input)) {
    return input.split("-").reverse().join(".")
  }

  if (!ISO_DATE_REGEX.test(input)) {
    return input
  }

  const date = new Date(input)
  return isDate(date) ? date : ""
}

export function isDateAfter(dateToCheck: string, referenceDate: string): boolean {
  if (!dateToCheck || !referenceDate) {
    return true
  }

  const dtCheck = new Date(dateToCheck)
  const dtRef = new Date(referenceDate)

  return dtCheck > dtRef
}
export function isDateBefore(dateToCheck: string, referenceDate: string): boolean {
  if (!dateToCheck || !referenceDate) {
    return true
  }
  const dtCheck = new Date(dateToCheck)
  const dtRef = new Date(referenceDate)
  return dtCheck < dtRef
}
export function isSameDate(dateToCheck: string, referenceDate: string): boolean {
  return dateToCheck === referenceDate
}

/**
 * Tests if a given date string is empty or `"0000-00-00"`
 * @param str The string to test
 * @returns If the string is empty
 */
export function isEmptyDate(str?: string | null) {
  return !str || str.trim() === "" || str === "0000-00-00"
}

/**
 * Converts a date string in yyyy-MM-dd format to locale format.
 */
export function toLocaleDateString(str = ""): string {
  if (isEmptyDate(str)) {
    return ""
  }
  return str.split("-").reverse().join(".")
}

// Normalize form values to Date or null - helps avoiding string vs Date mismatch with date inputs
export function toDateOrNull(v: Date | string | undefined | null): Date | null {
  if (v == null || v === "") return null
  const d = v instanceof Date ? v : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

export function toISODateString(date: Date | string | undefined | null) {
  if (!date) return null
  return dayjs(new Date(date)).format("YYYY-MM-DD")
}

export function toISOTimeString(date: Date | string = new Date()) {
  return new Date(date).toISOString().split("T")[1].slice(0, 5)
}

export function createISODateTimeFromString(
  dateStr: string | null,
  timeStr: string | null
): string {
  let year: number,
    month: number,
    day: number,
    hour = 0,
    minute = 0

  if (dateStr) {
    ;[year, month, day] = dateStr.split("-").map(Number)
  } else {
    // Set to "0000-00-00" equivalent
    year = 0
    month = 0
    day = 0
  }

  if (timeStr) {
    ;[hour, minute] = timeStr.split(":").map(Number)
  }

  // Handling the "0000-00-00" case to avoid invalid date
  if (year === 0 && month === 0 && day === 0) {
    return (
      "0000-00-00T" + `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`
    )
  }

  const date = new Date(Date.UTC(year, month - 1, day, hour, minute))
  return date.toISOString()
}
export function IsInvalidDate(dateTime: Date) {
  return isNaN(dateTime.getTime())
}
export function extractDateFromISO8601(isoString: string) {
  const dateTime = new Date(isoString)
  if (IsInvalidDate(dateTime)) {
    return ""
  }

  return toISODateString(dateTime)
}

export function extractTimeFromDate(date: Date) {
  return dayjs(date).format("HH:mm")
}

export function convertUTCDateTimeToLocalTime(
  utcDateTime: Date | string,
  timeZone?: string
): string {
  const parsedDateTime =
    typeof utcDateTime === "string" ? dayjs.utc(new Date(utcDateTime)) : dayjs.utc(utcDateTime)

  if (!parsedDateTime.isValid()) {
    return ""
  }
  if (!extractTimeFromISO8601(parsedDateTime.toISOString())) {
    return ""
  }
  try {
    const localDateTime = timeZone ? parsedDateTime.tz(timeZone) : parsedDateTime.local()
    const localTime = localDateTime.format("HH:mm")

    return localTime
  } catch {
    return ""
  }
}
export function extractTimeFromISO8601(isoString: string) {
  const dateTime = new Date(isoString)
  if (IsInvalidDate(dateTime)) {
    return ""
  }
  const time = toISOTimeString(dateTime)

  if (time === "00:00") {
    return ""
  }
  return time
}

export function formatDate(isoString: Dayjs | string | null | undefined) {
  if (!isoString) return ""
  return dayjs(isoString).format("DD.MM.YYYY")
}

export function formatToRfc3339(date: Date | string | null, time = "", tz = "") {
  const parsedDate = typeof date === "string" ? dayjs(date).utc() : dayjs(date)

  if (time && (!parsedDate.isValid() || !TIME_REGEX.test(time))) {
    return ""
  }

  const datePart = parsedDate.format("YYYY-MM-DD")

  const parsedDateTime = time
    ? dayjs.tz(`${datePart} ${time}`, tz).utc()
    : dayjs.tz(`${datePart}`, "UTC").utc()

  if (!parsedDateTime.isValid()) {
    return ""
  }

  const rfc3339DateTime = parsedDateTime.toISOString()
  return rfc3339DateTime
}

export function parseRfc3339ToIsoDate(value: string | Date | null | undefined): string | null {
  if (value == null) return null
  const d = dayjs.utc(value)
  return d.isValid() ? d.format("YYYY-MM-DD") : null
}

// Check if the first date is the same or after the second date
export function isAfterOrSame(date1: Date, date2: Date) {
  const day1 = dayjs(date1)
  const day2 = dayjs(date2)

  return day1.isSame(day2) || day1.isAfter(day2)
}
export function isDateOneYearAfterToday(date: Date) {
  const today = dayjs()
  const maxDate = today.add(1, "year")

  const dateToCheck = dayjs(date)

  return dateToCheck.isSame(today, "day") || dateToCheck.isBefore(maxDate)
}

export function calculateDateFromToday(years: number) {
  const today = new Date()
  const calculatedDate = new Date(today.getFullYear() + years, today.getMonth(), today.getDate())
  return calculatedDate
}

/**
 * Returns the first date of the same month and year as the provided date.
 *
 * @param {Date} date - The input date from which the first date of the month is derived.
 * @returns {Date} The first date of the month (same year and month as the input date).
 */
export function getFirstDateOfMonth(date: Date, asDate = false): Date | string | null {
  // Create a new date object with the same year and month, but set the date to 1
  const firstDate = new Date(date.getFullYear(), date.getMonth(), 1)
  return asDate ? firstDate : toISODateString(firstDate)
}

/**
 * Returns the last date of the same month and year as the provided date.
 *
 * @param {Date} date - The input date from which the last date of the month is derived.
 * @returns {Date} The last date of the month (same year and month as the input date).
 */
export function getLastDateOfMonth(date: Date, asDate = false): Date | string | null {
  // Create a new date object with the same year and the next month, set the date to 0
  // Setting the date to 0 will give us the last day of the previous month
  const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  return asDate ? lastDate : toISODateString(lastDate)
}

export function calculateDayDifference(birthDate: Date) {
  const today = new Date()
  // Set the birth date year to match today's year
  const adjustedBirthDate = new Date(birthDate)
  adjustedBirthDate.setFullYear(today.getFullYear())

  // Calculate the difference in days
  const difference = Math.floor(
    (today.getTime() - adjustedBirthDate.getTime()) / millisecondsPerDay
  )

  return difference !== 0 ? -difference : difference
}

export function calculateAge(birthDate: string) {
  return birthDate ? Math.floor(dayjs().diff(dayjs(birthDate), "year", true)) : -1
}

export const isWithinPeriod = (
  from?: string | null,
  to?: string | null,
  date?: string | Date
): boolean => {
  const current = date ? dayjs(date).startOf("day") : dayjs().startOf("day")
  const fromDate = from ? dayjs(from) : null
  const toDate = to ? dayjs(to) : null

  if (fromDate && current.isBefore(fromDate, "day")) return false
  if (toDate && current.isAfter(toDate, "day")) return false

  return true
}
export function isDateInRange(from: string, to?: string): boolean {
  if (!from) {
    return false
  }
  const today = dayjs().startOf("day")
  const fromDate = dayjs(from).startOf("day")

  if (!to) {
    return fromDate.isValid() && today.isSame(fromDate)
  }

  const toDate = dayjs(to).startOf("day")

  return today.isBetween(fromDate, toDate, "day", "[]")
}

export function sortByDate(
  dateA: string | Date,
  dateB: string | Date,
  dir: "asc" | "desc" = "asc"
): number {
  const x = new Date(dateA).getTime() - new Date(dateB).getTime()
  if (dir === "asc") return x
  else return -x
}

export function hasOverlapOrInside(
  from: Dayjs | string,
  to: Dayjs | string | null,
  yearStart: Dayjs,
  yearEnd: Dayjs
): boolean {
  const fromDate = ensureDayjs(from)
  const toDate = to ? ensureDayjs(to) : null

  return fromDate.isSameOrBefore(yearEnd) && (toDate === null || toDate.isSameOrAfter(yearStart))
}
export function isTodayWithinRange(from: string, to: string | null) {
  const todaysDate = dayjs().startOf("day")
  const lastDayOfCurrentYear = dayjs().endOf("year").format("YYYY-MM-DD")

  const fromDate = dayjs(from).startOf("day")
  const toDate = to ? dayjs(to).startOf("day") : dayjs(lastDayOfCurrentYear)

  return todaysDate.isBetween(fromDate, toDate, "day", "[]")
}
export const isCurrentDateRange = (from: string, to?: string) => {
  const today = dayjs()
  const validFrom = dayjs(from).isSameOrBefore(today, "day")
  if (!to) {
    return validFrom
  }
  const validTo = dayjs(to).isSameOrAfter(today, "day")
  return validFrom && validTo
}

export function isMonthInRange(month: Dayjs, from: dayjs.ConfigType, to: dayjs.ConfigType = null) {
  const startOfMonth = month.startOf("month")
  const endOfMonth = month.endOf("month")

  const fromDate = dayjs(from)
  const toDate = to ? dayjs(to) : null

  if (fromDate.isAfter(endOfMonth, "day")) return false

  if (!toDate) return fromDate.isSameOrBefore(endOfMonth, "day")

  return fromDate.isSameOrBefore(endOfMonth, "day") && toDate.isSameOrAfter(startOfMonth, "day")
}

export const isInDateRange = (date: Dayjs, from: string | Dayjs, to?: string | Dayjs) => {
  const validFrom = ensureDayjs(from).isSameOrBefore(date, "day")
  if (!to) {
    return validFrom
  }
  const validTo = ensureDayjs(to).isSameOrAfter(date, "day")
  return validFrom && validTo
}

export function getCoveredMonthsInYearRange(
  from: string | dayjs.Dayjs,
  to: string | dayjs.Dayjs,
  year: number
): number[] {
  const yearStart = dayjs(`${year}-01-01`)
  const yearEnd = dayjs(`${year}-12-31`)

  const start = dayjs(from).isBefore(yearStart) ? yearStart : dayjs(from).startOf("month")
  const end = dayjs(to).isAfter(yearEnd) ? yearEnd : dayjs(to).endOf("month")

  const months: number[] = []

  let current = start

  while (current.isSameOrBefore(end, "month")) {
    months.push(current.month())
    current = current.add(1, "month")
  }

  return months
}

export function isDateRangeOverlap(
  rangeStart: DateInput,
  rangeEnd: DateInput,
  periodStart: DateInput,
  periodEnd: DateInput
): boolean {
  if (!rangeStart || !periodStart) {
    return false
  }

  const start = dayjs(rangeStart)
  const end = rangeEnd ? dayjs(rangeEnd) : null
  const periodStartDayjs = dayjs(periodStart)
  const periodEndDayjs = periodEnd ? dayjs(periodEnd) : null

  const startsBeforePeriodEnds = !periodEndDayjs || start.isSameOrBefore(periodEndDayjs)
  const endsAfterPeriodStarts = !end || end.isSameOrAfter(periodStartDayjs)

  return startsBeforePeriodEnds && endsAfterPeriodStarts
}

/**
 * function to find the latest date from an array of dates
 * @param dates - Array of dates in various formats (Date, Dayjs, string, null, undefined)
 * @param unit - The unit of time to compare (default: 'day')
 * @returns The latest date as Dayjs object, or null if no valid dates found
 */
export function getLatestDate(
  dates: (Date | Dayjs | string | null | undefined)[],
  unit: dayjs.ManipulateType = "day"
): Dayjs | null {
  return dates.reduce<Dayjs | null>((latest, current) => {
    if (!current) return latest

    const currentDate = dayjs(current)
    if (!currentDate.isValid()) return latest

    return !latest || currentDate.isSameOrAfter(latest, unit) ? currentDate : latest
  }, null)
}

type DateRangeType =
  | "today"
  | "this_week"
  | "last_week"
  | "next_week"
  | "this_month"
  | "last_month"
  | "next_month"
  | "this_year"
  | "last_year"
  | "next_year"

export function getDateRangeFromRangeType(
  type: string | null | undefined,
  referenceDay: Dayjs = dayjs()
) {
  if (!type) return null
  const today = referenceDay
  let startDate: Dayjs
  let endDate: Dayjs
  switch (type as DateRangeType) {
    case "today":
      startDate = today.startOf("day")
      endDate = today.endOf("day")
      break
    case "this_week":
      startDate = today.startOf("week")
      endDate = today.endOf("week")
      break
    case "last_week":
      startDate = today.subtract(1, "week").startOf("week")
      endDate = today.subtract(1, "week").endOf("week")
      break
    case "next_week":
      startDate = today.add(1, "week").startOf("week")
      endDate = today.add(1, "week").endOf("week")
      break
    case "this_month":
      startDate = today.startOf("month")
      endDate = today.endOf("month")
      break
    case "last_month":
      startDate = today.subtract(1, "month").startOf("month")
      endDate = today.subtract(1, "month").endOf("month")
      break
    case "next_month":
      startDate = today.add(1, "month").startOf("month")
      endDate = today.add(1, "month").endOf("month")
      break
    case "this_year":
      startDate = today.startOf("year")
      endDate = today.endOf("year")
      break
    case "last_year":
      startDate = today.subtract(1, "year").startOf("year")
      endDate = today.subtract(1, "year").endOf("year")
      break
    case "next_year":
      startDate = today.add(1, "year").startOf("year")
      endDate = today.add(1, "year").endOf("year")
      break
    default:
      return null
  }
  return { startDate, endDate }
}

/*
 * Helper function to compare dates (null/undefined means open range)
 */
export function compareDates(
  a: Dayjs | Date | undefined,
  b: Dayjs | Date | undefined,
  unit: dayjs.OpUnitType = "day"
): number {
  if (!a && !b) return 0
  if (!a) return 1 // null/undefined is considered "later" (open range)
  if (!b) return -1

  const aDayjs = ensureDayjs(a)
  const bDayjs = ensureDayjs(b)

  if (aDayjs.isBefore(bDayjs, unit)) return -1
  if (aDayjs.isAfter(bDayjs, unit)) return 1
  return 0
}

export function getNextLeapYear(year: number) {
  while (!dayjs(`${year}-02-29`, "YYYY-MM-DD", true).isValid()) {
    year++
  }
  return formatISO(dayjs(`${year}-02-29`))
}

export const dayBefore = (iso: string) => dayjs(iso).subtract(1, "day").format("YYYY-MM-DD")

/*
 * Determines whether a given date is eligible for a booking or a presence
 * A date is considered valid if:
 * - it is not a weekend
 * - it is not included in the set of holiday dates
 * - it does not fall within any closing days range
 */
export function isDaySlotValid(date: Dayjs, holidayDates: Set<string>, closingDays: Appointment[]) {
  const dayOfWeek = date.day()
  const isWeekend = [Weekday.Saturday, Weekday.Sunday].includes(dayOfWeek)
  const isHoliday = holidayDates.has(formatISO(date))
  const isClosingDay = closingDays.some(({ from, to }) => {
    if (to) return isInDateRange(date, from, to)
    else return date.isSame(dayjs(from))
  })
  return !isWeekend && !isHoliday && !isClosingDay
}

export function isDateRangeContained(
  containerStart: DateInput,
  containerEnd: DateInput,
  itemStart: DateInput,
  itemEnd: DateInput
): boolean {
  if (!containerStart || !itemStart) return false

  const cStart = dayjs(containerStart).startOf("day")
  const cEnd = containerEnd ? dayjs(containerEnd).startOf("day") : null
  const iStart = dayjs(itemStart).startOf("day")
  const iEnd = itemEnd ? dayjs(itemEnd).startOf("day") : null

  if (!cStart.isValid() || !iStart.isValid()) return false
  if (cEnd && !cEnd.isValid()) return false
  if (iEnd && !iEnd.isValid()) return false

  const startOk = iStart.isSameOrAfter(cStart)
  const endOk = !cEnd ? true : !iEnd ? false : iEnd.isSameOrBefore(cEnd)

  return startOk && endOk
}
